import React, { useState } from "react";
import { URL as CLIENT_URL } from "../../config/config";
import axios from "axios";
import {
  Box,
  Typography,
  TextField,
  Button,
  Paper,
  Table,
  TableContainer,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TablePagination,
  IconButton,
  Tooltip,
  Collapse,
  CircularProgress,
  makeStyles,
} from "@material-ui/core";
import { Alert } from "@material-ui/lab";
import {
  KeyboardArrowDown,
  KeyboardArrowUp,
  Search as SearchIcon,
} from "@material-ui/icons";
import { Copy, Check, Search as SearchLucide, SearchX } from "lucide-react";

const BORDER = "#e0e0e0";

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(3),
    display: "flex",
    flexDirection: "column",
    height: "100%",
    boxSizing: "border-box",
    overflow: "hidden",
  },
  content: {
    flex: 1,
    minHeight: 0,
    overflowY: "auto",
  },
  title: {
    fontFamily: "Poppins",
    fontWeight: 600,
    fontSize: 22,
    color: theme.palette.text.primary,
    marginBottom: theme.spacing(2.5),
  },
  searchRow: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1.5),
    marginBottom: theme.spacing(3),
  },
  searchField: {
    width: 360,
    maxWidth: "100%",
  },
  context: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    flexWrap: "wrap",
    margin: theme.spacing(0, 0.5, 1.75),
    fontSize: 13,
    color: theme.palette.text.secondary,
  },
  contextCount: {
    fontWeight: 600,
    color: theme.palette.text.primary,
  },
  contextPid: {
    fontFamily: "monospace",
    fontSize: 12,
    color: theme.palette.text.secondary,
    background: theme.palette.grey[50],
    border: `1px solid ${BORDER}`,
    padding: "3px 9px",
    borderRadius: 6,
  },
  panel: {
    border: `1px solid ${BORDER}`,
    borderRadius: 10,
    overflow: "hidden",
    boxShadow: "none",
  },
  stateCard: {
    backgroundColor: theme.palette.grey[50],
    borderRadius: 10,
    boxShadow: "none",
    minHeight: "100%",
    boxSizing: "border-box",
    display: "flex",
    flexDirection: "column",
  },
  scroll: {
    overflowX: "auto",
    width: "100%",
  },
  table: {
    minWidth: 760,
  },
  pagination: {
    borderTop: `1px solid ${BORDER}`,
  },
  headCell: {
    backgroundColor: theme.palette.grey[50],
    fontWeight: 600,
    fontSize: 11.5,
    letterSpacing: "0.06em",
    textTransform: "uppercase",
    color: theme.palette.text.secondary,
    whiteSpace: "nowrap",
    textAlign: "center",
    borderBottom: `1px solid ${BORDER}`,
  },
  chevHead: {
    width: 44,
    backgroundColor: theme.palette.grey[50],
    borderBottom: `1px solid ${BORDER}`,
  },
  chevCell: {
    width: 44,
    textAlign: "center",
    color: theme.palette.text.secondary,
  },
  amount: {
    fontWeight: 600,
    color: theme.palette.primary.main,
    fontVariantNumeric: "tabular-nums",
    whiteSpace: "nowrap",
  },
  currency: {
    color: theme.palette.text.hint,
    fontWeight: 400,
    fontSize: 12,
    marginLeft: 3,
  },
  date: {
    whiteSpace: "nowrap",
    color: theme.palette.text.primary,
    textAlign: "center",
  },
  itemsSummary: {
    color: theme.palette.text.primary,
    textAlign: "center",
  },
  more: {
    color: theme.palette.text.secondary,
    fontSize: 12.5,
    marginLeft: 4,
  },
  mono: {
    fontFamily: "monospace",
    fontSize: 12.5,
    color: theme.palette.text.secondary,
    textAlign: "center",
  },
  copyWrap: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 4,
  },
  copyBtn: {
    padding: 3,
    color: theme.palette.text.hint,
    "&:hover": {
      color: theme.palette.primary.main,
      backgroundColor: "transparent",
    },
  },
  copiedIcon: {
    color: theme.palette.success.main,
  },
  empty: {
    flexGrow: 1,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    textAlign: "center",
    minHeight: 340,
    padding: theme.spacing(6, 3),
  },
  emptyIcon: {
    width: 60,
    height: 60,
    borderRadius: "50%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: theme.palette.background.paper,
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(2),
  },
  emptyTitle: {
    fontFamily: "Poppins",
    fontWeight: 600,
    fontSize: 16,
    color: theme.palette.text.primary,
    marginBottom: theme.spacing(0.5),
  },
  emptySubtext: {
    fontSize: 14,
    color: theme.palette.text.secondary,
    maxWidth: 380,
  },
  detailCell: {
    padding: 0,
    backgroundColor: theme.palette.grey[50],
  },
  detailInner: {
    padding: theme.spacing(0.5, 2, 1.75, 6.25),
  },
  detailTitle: {
    fontSize: 10.5,
    textTransform: "uppercase",
    letterSpacing: "0.08em",
    color: theme.palette.text.hint,
    padding: theme.spacing(1.5, 0, 0.5),
  },
  liTable: {
    maxWidth: 560,
  },
  liHeadCell: {
    fontWeight: 600,
    fontSize: 11,
    color: theme.palette.text.secondary,
    padding: "7px 0",
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  liCell: {
    fontSize: 13,
    padding: "8px 0",
    color: theme.palette.text.primary,
    borderBottom: "none",
  },
  liSummaryCell: {
    fontSize: 13,
    padding: "7px 0",
    color: theme.palette.text.secondary,
    borderBottom: "none",
  },
  liNumCell: {
    textAlign: "right",
    color: theme.palette.text.secondary,
    fontVariantNumeric: "tabular-nums",
    whiteSpace: "nowrap",
  },
  liTopBorder: {
    borderTop: `1px solid ${theme.palette.divider}`,
  },
  liTotalCell: {
    fontSize: 13.5,
    fontWeight: 700,
    color: theme.palette.text.primary,
    padding: "10px 0",
    borderTop: `1px solid ${BORDER}`,
    borderBottom: "none",
  },
  spinnerWrap: {
    flexGrow: 1,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    minHeight: 340,
    padding: theme.spacing(6),
  },
}));

const CURRENCY_SYMBOLS = { INR: "₹", USD: "$", EUR: "€", GBP: "£", AED: "د.إ" };

// Chargebee stores money in minor units (paise/cents).
const formatAmount = (amount, currency) => {
  if (amount === null || amount === undefined) return "-";
  const symbol = CURRENCY_SYMBOLS[currency] || "";
  const value = (amount / 100).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  return `${symbol}${value}`;
};

// date is a unix timestamp in seconds. Always render in India time (IST),
const formatDate = (date) => {
  if (!date) return "-";
  return new Date(date * 1000).toLocaleString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    timeZone: "Asia/Kolkata",
  });
};

const CopyableId = ({ value }) => {
  const classes = useStyles();
  const [copied, setCopied] = useState(false);

  if (!value) return <span>-</span>;

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 1200);
    } catch (err) {
      console.error("Copy failed:", err);
    }
  };

  return (
    <span className={classes.copyWrap}>
      <span>{value}</span>
      <Tooltip title={copied ? "Copied!" : "Copy"}>
        <IconButton
          size="small"
          className={classes.copyBtn}
          onClick={handleCopy}
          aria-label={`Copy ${value}`}
        >
          {copied ? (
            <Check size={15} className={classes.copiedIcon} />
          ) : (
            <Copy size={15} />
          )}
        </IconButton>
      </Tooltip>
    </span>
  );
};

const PaymentRow = ({ payment }) => {
  const classes = useStyles();
  const [open, setOpen] = useState(false);
  const lineItems = payment.lineItems || [];
  const taxes = payment.taxes || [];
  const firstItem = lineItems[0];
  const hasMultiple = lineItems.length > 1;
  // Worth expanding only when the breakdown says more than the headline amount:
  // several line items, or tax billed on top of a single item.
  const hasBreakdown = hasMultiple || taxes.length > 0;

  const lineItemsSum = lineItems.reduce(
    (sum, li) => sum + (typeof li.amount === "number" ? li.amount : 0),
    0
  );
  const subTotal = payment.subTotal != null ? payment.subTotal : lineItemsSum;
  const grandTotal =
    payment.invoiceTotal != null ? payment.invoiceTotal : payment.amount;

  return (
    <>
      <TableRow hover>
        <TableCell className={classes.chevCell}>
          {hasBreakdown && (
            <IconButton
              size="small"
              aria-label={open ? "Collapse breakdown" : "Expand breakdown"}
              onClick={() => setOpen(!open)}
            >
              {open ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
            </IconButton>
          )}
        </TableCell>
        <TableCell className={classes.date}>{formatDate(payment.date)}</TableCell>
        <TableCell align="center">
          <span className={classes.amount}>
            {formatAmount(payment.amount, payment.currency)}
            {payment.currency && (
              <span className={classes.currency}>{payment.currency}</span>
            )}
          </span>
        </TableCell>
        <TableCell className={classes.itemsSummary}>
          {firstItem ? firstItem.description || "(no description)" : "—"}
          {hasMultiple && (
            <span className={classes.more}>+ {lineItems.length - 1} more</span>
          )}
        </TableCell>
        <TableCell className={classes.mono}>
          <CopyableId value={payment.paymentId} />
        </TableCell>
        <TableCell className={classes.mono}>
          <CopyableId value={payment.invoiceId} />
        </TableCell>
      </TableRow>
      {hasBreakdown && (
        <TableRow>
          <TableCell className={classes.detailCell} colSpan={6}>
            <Collapse in={open} timeout="auto" unmountOnExit>
              <div className={classes.detailInner}>
                <Typography className={classes.detailTitle}>
                  Order summary
                </Typography>
                <Table size="small" className={classes.liTable}>
                  <TableHead>
                    <TableRow>
                      <TableCell className={classes.liHeadCell}>
                        Description
                      </TableCell>
                      <TableCell
                        className={`${classes.liHeadCell} ${classes.liNumCell}`}
                      >
                        Amount
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {lineItems.map((li, idx) => (
                      <TableRow key={idx}>
                        <TableCell className={classes.liCell}>
                          {li.description || "(no description)"}
                        </TableCell>
                        <TableCell
                          className={`${classes.liCell} ${classes.liNumCell}`}
                        >
                          {formatAmount(li.amount, payment.currency)}
                        </TableCell>
                      </TableRow>
                    ))}
                    <TableRow>
                      <TableCell
                        className={`${classes.liSummaryCell} ${classes.liTopBorder}`}
                      >
                        Subtotal
                      </TableCell>
                      <TableCell
                        className={`${classes.liSummaryCell} ${classes.liTopBorder} ${classes.liNumCell}`}
                      >
                        {formatAmount(subTotal, payment.currency)}
                      </TableCell>
                    </TableRow>
                    {taxes.map((tax, idx) => (
                      <TableRow key={`tax-${idx}`}>
                        <TableCell className={classes.liSummaryCell}>
                          {tax.name}
                        </TableCell>
                        <TableCell
                          className={`${classes.liSummaryCell} ${classes.liNumCell}`}
                        >
                          {formatAmount(tax.amount, payment.currency)}
                        </TableCell>
                      </TableRow>
                    ))}
                    <TableRow>
                      <TableCell className={classes.liTotalCell}>Total</TableCell>
                      <TableCell
                        className={`${classes.liTotalCell} ${classes.liNumCell}`}
                      >
                        {formatAmount(grandTotal, payment.currency)}
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </div>
            </Collapse>
          </TableCell>
        </TableRow>
      )}
    </>
  );
};

const PaymentsDashboard = () => {
  const classes = useStyles();
  const [assistantId, setAssistantId] = useState("");
  const [searchedId, setSearchedId] = useState("");
  const [payments, setPayments] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [searched, setSearched] = useState(false);

  const fetchPayments = async (id, pageIndex, perPage) => {
    setLoading(true);
    setError("");

    try {
      const response = await axios.get(
        `${CLIENT_URL}/superadmin/payment-analytics/payments`,
        { params: { assistantId: id, page: pageIndex + 1, limit: perPage } }
      );
      setPayments(response.data.data || []);
      setTotal(response.data.total || 0);
    } catch (err) {
      console.error("Error fetching payments:", err);
      setError("Failed to fetch payments for this assistant.");
      setPayments([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    const id = assistantId.trim();
    if (!id) return;

    setSearched(true);
    setSearchedId(id);
    setPage(0);
    fetchPayments(id, 0, rowsPerPage);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
    fetchPayments(searchedId, newPage, rowsPerPage);
  };

  const handleChangeRowsPerPage = (event) => {
    const perPage = parseInt(event.target.value, 10);
    setRowsPerPage(perPage);
    setPage(0);
    fetchPayments(searchedId, 0, perPage);
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") handleSearch();
  };

  return (
    <Box className={classes.root}>
      <Typography className={classes.title}>Payments Dashboard</Typography>

      <div className={classes.searchRow}>
        <TextField
          label="Assistant ID"
          variant="outlined"
          size="small"
          value={assistantId}
          onChange={(e) => setAssistantId(e.target.value)}
          onKeyDown={handleKeyDown}
          className={classes.searchField}
        />
        <Button
          variant="contained"
          color="primary"
          disableElevation
          startIcon={<SearchIcon />}
          onClick={handleSearch}
          disabled={loading || !assistantId.trim()}
        >
          Search
        </Button>
      </div>

      <div className={classes.content}>
        {loading && (
        <Paper className={classes.stateCard}>
          <div className={classes.spinnerWrap}>
            <CircularProgress />
          </div>
        </Paper>
      )}

      {!loading && error && <Alert severity="error">{error}</Alert>}

      {!loading && !error && !searched && (
        <Paper className={classes.stateCard}>
          <div className={classes.empty}>
            <div className={classes.emptyIcon}>
              <SearchLucide size={26} strokeWidth={1.8} />
            </div>
            <Typography className={classes.emptyTitle}>
              Search an assistant's payments
            </Typography>
            <Typography className={classes.emptySubtext}>
              Paste an Assistant ID above and hit Search to view its successful
              payments, line items and invoices.
            </Typography>
          </div>
        </Paper>
      )}

      {!loading && !error && searched && payments.length === 0 && (
        <Paper className={classes.stateCard}>
          <div className={classes.empty}>
            <div className={classes.emptyIcon}>
              <SearchX size={26} strokeWidth={1.8} />
            </div>
            <Typography className={classes.emptyTitle}>
              No payments found
            </Typography>
            <Typography className={classes.emptySubtext}>
              We couldn't find any successful payments for this assistant. Double
              check the Assistant ID and try again.
            </Typography>
          </div>
        </Paper>
      )}

      {!loading && !error && payments.length > 0 && (
        <>
          <div className={classes.context}>
            <span className={classes.contextCount}>
              {total} {total === 1 ? "payment" : "payments"}
            </span>
            <span>found for assistant</span>
            <span className={classes.contextPid}>{searchedId}</span>
          </div>

          <Paper className={classes.panel}>
            <TableContainer className={classes.scroll}>
              <Table className={classes.table}>
                <TableHead>
                  <TableRow>
                    <TableCell className={classes.chevHead} />
                    <TableCell className={classes.headCell}>Date</TableCell>
                    <TableCell className={classes.headCell}>Amount</TableCell>
                    <TableCell className={classes.headCell}>Line items</TableCell>
                    <TableCell className={classes.headCell}>Payment ID</TableCell>
                    <TableCell className={classes.headCell}>Invoice ID</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {payments.map((payment, idx) => (
                    <PaymentRow
                      key={payment.paymentId || idx}
                      payment={payment}
                    />
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              className={classes.pagination}
              component="div"
              count={total}
              page={page}
              onPageChange={handleChangePage}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              rowsPerPageOptions={[10, 25, 50]}
            />
          </Paper>
        </>
      )}
      </div>
    </Box>
  );
};

export default PaymentsDashboard;
